<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { readReasonKey } from '@/domain/menu-import/workspace-view';
  import type { IRetakePage } from '@/domain/menu-import/workspace-view';

  /**
   * «No pudimos leer la página 2.» (plan §4.4): a page of a read that found
   * dishes elsewhere, with the reason and what can be done: «Intentar de
   * nuevo» when the reading failed on our side (the stored images are read
   * again, nothing is uploaded), «Sacar otra foto» for a photo (the next read
   * merges it), and «Seguir sin ella». A page read with too many dishes
   * (`TOO_DENSE`) has dishes already: no «Seguir sin ella».
   */
  const props = defineProps<{
    entry: IRetakePage;
    /** A read or an action is running, or the device is offline. */
    disabled: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'retry'): void;
    (e: 'retake'): void;
    (e: 'dismiss'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;

  const reason = computed(() => {
    const key = readReasonKey(props.entry.errorCode);
    return key === null ? null : String(proxy.$t(key));
  });
  /** A part of a very tall page read in parts: «una parte de la página 2». */
  const titleKey = computed(() =>
    props.entry.inParts
      ? 'menuImport.pageFailed.titlePart'
      : 'menuImport.pageFailed.title'
  );
  /** A whole photo can be taken again; a PDF page or a long page could only be added again whole. */
  const isPhoto = computed(
    () =>
      !props.entry.inParts &&
      (props.entry.origin === 'camera' || props.entry.origin === 'gallery')
  );
</script>

<template>
  <section class="v2-menu-page-failed-card" role="status">
    <PosUiIcon name="image-broken-variant" class="card-icon" />
    <div class="card-main">
      <p class="card-title">
        {{ $t(titleKey, { page: entry.pageNumber }) }}
      </p>
      <p v-if="reason" class="card-reason">{{ reason }}</p>
      <div class="card-actions">
        <PosUiButton
          v-if="entry.retryable"
          color="neutral"
          variant="outline"
          icon="refresh"
          :disabled="disabled"
          @click="emit('retry')"
          >{{ $t('menuImport.failed.retry') }}</PosUiButton
        >
        <PosUiButton
          v-else-if="isPhoto"
          color="neutral"
          variant="outline"
          icon="camera-outline"
          @click="emit('retake')"
          >{{ $t('menuImport.pageFailed.retake') }}</PosUiButton
        >
        <PosUiButton
          v-if="entry.canSkip"
          color="neutral"
          variant="ghost"
          @click="emit('dismiss')"
          >{{ $t('menuImport.pageFailed.skip') }}</PosUiButton
        >
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
  .v2-menu-page-failed-card {
    @apply tw-flex tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-amber-200 tw-bg-amber-50 tw-p-4;
    .card-icon {
      @apply tw-mt-0.5 tw-shrink-0 tw-text-xl tw-text-amber-700;
    }
    .card-main {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1;
    }
    .card-title {
      @apply tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900;
    }
    .card-reason {
      @apply tw-m-0 tw-text-sm tw-text-neutral-700;
    }
    .card-actions {
      @apply tw-mt-2 tw-flex tw-flex-wrap tw-gap-2;
    }
  }
</style>
